import React, { useState, useEffect, useCallback, useRef } from 'react'
import messageIcon from "../../img/messageIcon.svg"
import phoneIcon from "../../img/phoneIcon.svg"

/* ── field normalisation ── */
const displayStatus = (s) => {
  if (!s) return '—'
  const map = { not_assigned: 'Not Assigned', assigned: 'Assigned', re_assigned: 'Re-Assigned', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold', re_opened: 'Re-Opened', resolved_verification_pending: 'Resolved - Verification Pending', assigned_started: 'Assigned - Started', assigned_not_started: 'Assigned - Not Started', rider_re_opened: 'Rider Re-Opened', system_auto_closed: 'System Auto Closed' }
  return map[s] || s
}
const displayPriority = (p) => {
  if (!p) return '—'
  return p.charAt(0).toUpperCase() + p.slice(1)
}
const displayChannel = (c) => {
  if (!c) return '—'
  const map = { call: 'Phone Call', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email' }
  return map[c] || c
}
const displayStage = (s) => {
  if (!s) return '—'
  return s.toUpperCase()
}
const formatCreatedParts = (t) => {
  if (t.created_time && t.created_date) return { time: t.created_time, date: t.created_date }
  const raw = t.created_at || t.created_date || t.created_time
  if (raw) {
    const d = new Date(raw)
    if (!Number.isNaN(d.getTime())) {
      return {
        time: t.created_time || d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        date: t.created_date || d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      }
    }
  }
  return { time: t.created_time || t.created_date || '—', date: '' }
}

const PRIORITY_OPTIONS   = ['All Priority', 'urgent', 'normal']
const STATUS_OPTIONS     = ['All Status', 'opened', 'not_assigned', 'assigned', 're_assigned', 'in_progress', 'on_hold', 'resolved', 'resolved_verification_pending', 'closed', 're_opened', 'assigned_started', 'assigned_not_started', 'rider_re_opened', 'system_auto_closed']
const CHANNEL_OPTIONS    = ['All Channels', 'call', 'whatsapp', 'rider_app', 'email']
const ESCALATION_OPTIONS = ['All Escalation', 'l1', 'l2', 'l3', 'l4']
const SUB_TICKET_TYPE_OPTIONS = ['All Sub Ticket Type', 'new_ticket', 'query_ticket']

const statusClass = (s) => {
  if (s === 'not_assigned') return 'not-assigned'
  if (s === 'assigned')    return 'assigned'
  if (s === 're_assigned') return 're-assigned'
  if (s === 'in_progress') return 'in-progress'
  if (s === 'resolved')    return 'resolved'
  if (s === 'resolved_verification_pending') return 'verification-pending'
  if (s === 'on_hold')     return 'on-hold'
  if (s === 'closed')      return 'closed'
  if (s === 're_opened')   return 're-opened'
  if (s === 'assigned_started')     return 'assigned-started'
  if (s === 'assigned_not_started') return 'assigned-not-started'
  if (s === 'rider_re_opened')      return 'rider-re-opened'
  if (s === 'system_auto_closed')   return 'system-auto-closed'
  return ''
}

const channelIcon = (ch) => {
  if (ch === 'call')      return <img src={phoneIcon} width={16} height={16} alt="phone" className="t360-channel-img" />
  if (ch === 'whatsapp')  return <img src={messageIcon} width={16} height={16} alt="message" />
  if (ch === 'rider_app') return '📱'
  if (ch === 'email')     return '✉️'
  return '🔗'
}

const T360_STORAGE_KEY = 't360_filters_v1'

function loadPersistedFilters() {
  try {
    const raw = sessionStorage.getItem(T360_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

// "All Sub-Categories" -> "Sub-Categories", so a multi-select's closed label
// reads "Sub-Categories (3)" instead of an anonymous "3 selected".
const filterBaseName = (placeholder) => placeholder.replace(/^All /, '')

function MultiSelectSearchDropdown({ fetchJson, values, placeholder, apiUrl, onChange, disabled }) {
  const [open, setOpen]       = useState(false)
  const [items, setItems]     = useState([])
  const [search, setSearch]   = useState('')
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage]       = useState(1)
  const wrapRef               = useRef(null)
  const listRef               = useRef(null)
  const loadingRef            = useRef(false)

  const load = useCallback(async (pg = 1, append = false) => {
    if (!fetchJson || loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(pg) })
      const data = await fetchJson(`${apiUrl}${apiUrl.includes('?') ? '&' : '?'}${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setItems(prev => append ? [...prev, ...results] : results)
      setHasMore(!!data?.next)
      setPage(pg)
    } catch { if (!append) setItems([]) }
    finally { loadingRef.current = false; setLoading(false) }
  }, [fetchJson, apiUrl])

  useEffect(() => {
    if (open) load(1, false)
  }, [open, load])

  useEffect(() => {
    if (!open) return
    function handler(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasMore || loading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) load(page + 1, true)
  }

  const filtered = search.trim()
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items

  const isSelected = slug => values.some(v => v.slug === slug)
  const toggle = (item) => {
    if (isSelected(item.slug)) onChange(values.filter(v => v.slug !== item.slug))
    else onChange([...values, item])
  }

  const label = values.length === 0 ? placeholder
    : values.length === 1 ? values[0].name
    : `${filterBaseName(placeholder)} (${values.length})`

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => { if (!disabled) setOpen(o => !o) }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '6px 10px', background: values.length ? '#E5F4FB' : '#fff',
          border: `1px solid ${values.length ? '#A8D4EE' : '#E0E7EF'}`, borderRadius: 6,
          cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500,
          color: values.length ? '#1A6FA8' : '#5A6672', whiteSpace: 'nowrap', minWidth: 130,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>{label}</span>
        <span style={{ fontSize: 9, flexShrink: 0 }}>▼</span>
      </div>
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 210,
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 12, color: '#111827', boxSizing: 'border-box' }}
            />
          </div>
          {values.length > 0 && (
            <div
              onClick={() => onChange([])}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Clear all ({values.length})
            </div>
          )}
          <div ref={listRef} onScroll={handleScroll} style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.map(item => {
              const selected = isSelected(item.slug)
              return (
                <div
                  key={item.id || item.slug}
                  onClick={() => toggle(item)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#111827',
                    background: selected ? '#EFF6FF' : undefined,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = selected ? '#DBEAFE' : '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = selected ? '#EFF6FF' : '#fff'}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    style={{
                      pointerEvents: 'none', width: 14, height: 14, flexShrink: 0,
                      padding: 0, margin: 0, border: '1px solid #CBD5E1', borderRadius: 3,
                      background: '#fff', accentColor: '#2563EB',
                    }}
                  />
                  {item.name}
                </div>
              )
            })}
            {loading && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>}
            {!loading && filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>No results</div>}
            {hasMore && !loading && <div style={{ padding: '6px 12px', fontSize: 10, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll for more</div>}
          </div>
        </div>
      )}
    </div>
  )
}

function MultiSelectDropdown({ values, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function handler(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter(v => v !== val))
    else onChange([...values, val])
  }

  const label = values.length === 0 ? placeholder
    : values.length === 1 ? (options.find(o => o.value === values[0])?.label || values[0])
    : `${filterBaseName(placeholder)} (${values.length})`

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '6px 10px', background: values.length ? '#E5F4FB' : '#fff',
          border: `1px solid ${values.length ? '#A8D4EE' : '#E0E7EF'}`, borderRadius: 6,
          cursor: 'pointer', fontSize: 12, fontWeight: 500,
          color: values.length ? '#1A6FA8' : '#5A6672', whiteSpace: 'nowrap', minWidth: 130,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>{label}</span>
        <span style={{ fontSize: 9, flexShrink: 0 }}>▼</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 180,
        }}>
          {values.length > 0 && (
            <div
              onClick={() => onChange([])}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Clear all ({values.length})
            </div>
          )}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {options.map(opt => {
              const selected = values.includes(opt.value)
              return (
                <div
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#111827',
                    background: selected ? '#EFF6FF' : undefined,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = selected ? '#DBEAFE' : '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = selected ? '#EFF6FF' : '#fff'}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    style={{
                      pointerEvents: 'none', width: 14, height: 14, flexShrink: 0,
                      padding: 0, margin: 0, border: '1px solid #CBD5E1', borderRadius: 3,
                      background: '#fff', accentColor: '#2563EB',
                    }}
                  />
                  {opt.label}
                </div>
              )
            })}
            {options.length === 0 && <div style={{ padding: '8px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>No options</div>}
          </div>
        </div>
      )}
    </div>
  )
}

const PRIORITY_LABELS   = { 'All Priority': 'All Priority', urgent: 'Urgent', normal: 'Normal' }
const STATUS_LABELS     = { 'All Status': 'All Status', opened: 'Opened', not_assigned: 'Not Assigned', assigned: 'Assigned', re_assigned: 'Re-Assigned', in_progress: 'In Progress', on_hold: 'On Hold', resolved: 'Resolved', resolved_verification_pending: 'Resolved - Verification Pending', closed: 'Closed', re_opened: 'Re-Opened', assigned_started: 'Assigned - Started', assigned_not_started: 'Assigned - Not Started', rider_re_opened: 'Rider Re-Opened', system_auto_closed: 'System Auto Closed' }
const CHANNEL_LABELS    = { 'All Channels': 'All Channels', call: 'Phone Call', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email' }
const ESCALATION_LABELS = { 'All Escalation': 'All Escalation', l1: 'L1', l2: 'L2', l3: 'L3', l4: 'L4' }
const SUB_TICKET_TYPE_LABELS = { 'All Sub Ticket Type': 'All Sub Ticket Type', new_ticket: 'New Ticket', query_ticket: 'Query Ticket' }

const toMultiOptions = (options, labels) => options.slice(1).map(v => ({ value: v, label: labels[v] || v }))
const PRIORITY_MULTI_OPTIONS   = toMultiOptions(PRIORITY_OPTIONS, PRIORITY_LABELS)
const STATUS_MULTI_OPTIONS     = toMultiOptions(STATUS_OPTIONS, STATUS_LABELS)
const CHANNEL_MULTI_OPTIONS    = toMultiOptions(CHANNEL_OPTIONS, CHANNEL_LABELS)
const ESCALATION_MULTI_OPTIONS = toMultiOptions(ESCALATION_OPTIONS, ESCALATION_LABELS)
const SUB_TICKET_TYPE_MULTI_OPTIONS = toMultiOptions(SUB_TICKET_TYPE_OPTIONS, SUB_TICKET_TYPE_LABELS)

const DATE_OPTIONS = [
  { period: 'today',     label: 'Today' },
  { period: 'yesterday', label: 'Yesterday' },
  { period: '7days',    label: 'Last 7 Days' },
  { period: '30days',   label: 'Last 30 Days' },
  { period: '6months',  label: 'Last 6 Months' },
]

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

export default function Ticket360Page({ setCurrentPage, setSelectedTicket, fetchJson, currentUser }) {
  const isErtUser = Array.isArray(currentUser?.feature_access) && currentUser.feature_access.includes('ert_page')

  // Filters persist across navigation (e.g. opening a ticket and coming back)
  // via sessionStorage, read once per mount.
  const persistedRef = useRef(null)
  if (persistedRef.current === null) persistedRef.current = loadPersistedFilters()
  const persisted = persistedRef.current

  const [tickets, setTickets]         = useState([])
  const [loading, setLoading]         = useState(false)
  const [totalCount, setTotalCount]   = useState(0)
  const [page, setPage]               = useState(persisted.page || 1)
  const [pageSize, setPageSize]       = useState(persisted.pageSize || 10)
  const [search,            setSearch]            = useState(persisted.search || '')
  const [priorityFilters,   setPriorityFilters]   = useState(persisted.priorityFilters || [])
  const [statusFilters,     setStatusFilters]     = useState(persisted.statusFilters || [])
  const [channelFilters,    setChannelFilters]    = useState(persisted.channelFilters || [])
  const [escalationFilters, setEscalationFilters] = useState(persisted.escalationFilters || [])
  const [subTicketTypeFilters, setSubTicketTypeFilters] = useState(persisted.subTicketTypeFilters || [])
  const [technicianFilters, setTechnicianFilters] = useState(persisted.technicianFilters || [])
  const [technicianOptions, setTechnicianOptions] = useState([])
  const [myTickets,  setMyTickets]    = useState(persisted.myTickets || false)
  const [bulkCloseOpen,   setBulkCloseOpen]   = useState(false)
  const [bulkClosing,     setBulkClosing]     = useState(false)
  const [refreshKey,      setRefreshKey]      = useState(0)
  const [showFilters,     setShowFilters]     = useState(persisted.showFilters || false)
  const [catFilters,       setCatFilters]       = useState(persisted.catFilters || [])
  const [subCatFilters,    setSubCatFilters]    = useState(persisted.subCatFilters || [])
  const [cityFilters,      setCityFilters]      = useState(persisted.cityFilters || [])
  const [stationFilters,   setStationFilters]   = useState(persisted.stationFilters || [])
  const [downloadLoading,  setDownloadLoading]  = useState(false)
  const [downloadDropOpen, setDownloadDropOpen] = useState(false)
  const downloadPollRef  = useRef(null)
  const downloadDropRef  = useRef(null)
  const [dateFilter, setDateFilter]   = useState(() => persisted.dateFilter || { period: 'today', label: 'Today', fromDate: todayISO(), toDate: todayISO() })
  const [dateOpen,   setDateOpen]     = useState(false)
  const [customFrom, setCustomFrom]   = useState('')
  const [customTo,   setCustomTo]     = useState('')
  const dateRef = useRef(null)

  // Fetch the full ERT technician list once, paging through agent-management until exhausted
  useEffect(() => {
    if (!isErtUser) return
    let cancelled = false
    const loadTechnicians = async () => {
      const all = []
      let page = 1
      try {
        while (true) {
          const params = new URLSearchParams({ team: 'ert', page: String(page), page_size: '100' })
          const res = await fetchJson(`/api/agents/agent-management/?${params.toString()}`)
          const list = Array.isArray(res) ? res : (res?.data || res?.results || [])
          all.push(...list)
          if (cancelled || !list.length || (!Array.isArray(res) && !res?.next)) break
          page += 1
        }
        if (!cancelled) setTechnicianOptions(all.filter(t => t.id).map(t => ({ value: t.id, label: t.name || t.phone || t.id })))
      } catch {
        if (!cancelled) setTechnicianOptions([])
      }
    }
    loadTechnicians()
    return () => { cancelled = true }
  }, [fetchJson, isErtUser])

  // Persist filters to sessionStorage so they survive navigating away and back
  useEffect(() => {
    const toSave = {
      page, pageSize, search, priorityFilters, statusFilters, channelFilters, escalationFilters,
      technicianFilters, myTickets, showFilters, catFilters, subCatFilters, cityFilters, stationFilters, dateFilter,
      subTicketTypeFilters,
    }
    try { sessionStorage.setItem(T360_STORAGE_KEY, JSON.stringify(toSave)) } catch {}
  }, [page, pageSize, search, priorityFilters, statusFilters, channelFilters, escalationFilters, technicianFilters, myTickets, showFilters, catFilters, subCatFilters, cityFilters, stationFilters, dateFilter, subTicketTypeFilters])

  useEffect(() => {
    if (!dateOpen) return
    function handler(e) { if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dateOpen])

  useEffect(() => {
    if (!downloadDropOpen) return
    function handler(e) { if (downloadDropRef.current && !downloadDropRef.current.contains(e.target)) setDownloadDropOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [downloadDropOpen])

  function selectPeriod(opt) {
    const today = new Date()
    const fmt = d => d.toISOString().slice(0, 10)
    let fromDate = fmt(today)
    let toDate = fmt(today)
    if (opt.period === 'yesterday') {
      const d = new Date(today); d.setDate(d.getDate() - 1)
      fromDate = fmt(d); toDate = fmt(d)
    }
    if (opt.period === '7days')   { const d = new Date(today); d.setDate(d.getDate() - 6);  fromDate = fmt(d) }
    if (opt.period === '30days')  { const d = new Date(today); d.setDate(d.getDate() - 29); fromDate = fmt(d) }
    if (opt.period === '6months') { const d = new Date(today); d.setMonth(d.getMonth() - 6); fromDate = fmt(d) }
    setDateFilter({ period: opt.period, label: opt.label, fromDate, toDate })
    setDateOpen(false)
  }

  function applyCustom() {
    if (!customFrom || !customTo) return
    setDateFilter({ period: 'custom', label: `${customFrom} – ${customTo}`, fromDate: customFrom, toDate: customTo })
    setDateOpen(false)
  }

  // Reset to page 1 whenever filters, search, or page size change — but not on
  // the initial mount, since that would stomp a persisted page number.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    setPage(1)
  }, [search, priorityFilters, statusFilters, channelFilters, escalationFilters, technicianFilters, pageSize, dateFilter, myTickets, catFilters, subCatFilters, cityFilters, stationFilters, subTicketTypeFilters])

  // Shared by fetchTickets/handleDownload/handleBulkClose so every action
  // that acts on "the current table view" honors the same set of filters.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams()
    if (search.trim())                              params.set('q', search.trim())
    if (priorityFilters.length)                      params.set('priority', priorityFilters.join(','))
    if (statusFilters.length)                        params.set('status', statusFilters.join(','))
    if (channelFilters.length)                       params.set('channel', channelFilters.join(','))
    if (escalationFilters.length)                    params.set('escalated_to', escalationFilters.join(','))
    if (isErtUser && technicianFilters.length)       params.set('assigned_to', technicianFilters.join(','))
    if (dateFilter.fromDate)   params.set('from_date',    dateFilter.fromDate)
    if (dateFilter.toDate)     params.set('to_date',      dateFilter.toDate)
    if (catFilters.length)     params.set('category',     catFilters.map(c => c.slug).join(','))
    if (subCatFilters.length)  params.set('sub_category', subCatFilters.map(c => c.slug).join(','))
    if (cityFilters.length)    params.set('incident_city', cityFilters.map(c => c.slug).join(','))
    if (stationFilters.length) params.set('station_id', stationFilters.map(s => s.slug).join(','))
    if (subTicketTypeFilters.length) params.set('sub_ticket_type', subTicketTypeFilters.join(','))
    params.set('my_tickets', myTickets ? 'true' : 'false')
    return params
  }, [search, priorityFilters, statusFilters, channelFilters, escalationFilters, technicianFilters, isErtUser, dateFilter, catFilters, subCatFilters, cityFilters, stationFilters, myTickets, subTicketTypeFilters])

  const fetchTickets = useCallback(() => {
    if (!fetchJson) return
    const params = buildFilterParams()
    params.set('page', String(page))
    params.set('page_size', String(pageSize))

    setLoading(true)
    fetchJson(`/api/tickets/ticket/?${params.toString()}`)
      .then(data => {
        const rows = Array.isArray(data) ? data : (data?.results || [])
        setTickets(rows)
        setTotalCount(Array.isArray(data) ? rows.length : (data?.count ?? rows.length))
      })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false))
  }, [fetchJson, buildFilterParams, page, pageSize, refreshKey])

  useEffect(() => {
    const t = setTimeout(fetchTickets, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [fetchTickets])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket)
    setCurrentPage('ticket-detail')
  }

  const handleBulkClose = async () => {
    setBulkClosing(true)
    try {
      const params = buildFilterParams()
      await fetchJson(`/api/tickets/ticket/bulk-close/?${params.toString()}`, { method: 'POST' })
      setPage(1)
      setRefreshKey(k => k + 1)
    } catch {
    } finally {
      setBulkClosing(false)
      setBulkCloseOpen(false)
    }
  }

  const handleClear = () => {
    setSearch('')
    setPriorityFilters([])
    setStatusFilters([])
    setChannelFilters([])
    setEscalationFilters([])
    setTechnicianFilters([])
    setDateFilter({ period: '', label: 'All Dates', fromDate: '', toDate: '' })
    setCustomFrom('')
    setCustomTo('')
    setCatFilters([])
    setSubCatFilters([])
    setCityFilters([])
    setStationFilters([])
    setSubTicketTypeFilters([])
    setPage(1)
  }

  // clear poll on unmount
  useEffect(() => () => { if (downloadPollRef.current) clearInterval(downloadPollRef.current) }, [])

  const handleDownload = async (fileType) => {
    setDownloadDropOpen(false)
    const params = buildFilterParams()
    params.set('file_type', fileType)

    setDownloadLoading(true)
    try {
      const data = await fetchJson(`/api/tickets/ticket/download-initiate/?${params.toString()}`)
      const taskId = data?.task_id
      if (!taskId) throw new Error('No task_id returned')

      downloadPollRef.current = setInterval(async () => {
        try {
          const resp = await fetch(`/api/tickets/ticket/download?task_id=${taskId}`, { credentials: 'include' })
          if (resp.status === 200) {
            clearInterval(downloadPollRef.current)
            downloadPollRef.current = null
            setDownloadLoading(false)
            const result = await resp.json()
            if (result?.file_url) {
              const a = document.createElement('a')
              a.href = result.file_url
              a.download = ''
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            }
          }
          // 202 = still processing, continue polling
        } catch { /* continue polling */ }
      }, 3000)
    } catch (err) {
      setDownloadLoading(false)
      alert(err?.message || 'Failed to initiate download.')
    }
  }

  return (
    <div className="t360-page">
      <p className="t360-subheading">
        Complete ticket intelligence — all attributes, evidence, lifecycle &amp; SLA signals in one view
      </p>

      {/* ── Row 1: always visible ── */}
      <div className="t360-filters">
        {/* My Tickets / All Tickets toggle */}
        <div style={{
          display: 'flex', flexShrink: 0,
          border: '1px solid #D1D5DB', borderRadius: 8, overflow: 'hidden',
        }}>
          {[{ label: 'My Tickets', value: true }, { label: 'All Tickets', value: false }].map(opt => (
            <button
              key={String(opt.value)}
              onClick={() => setMyTickets(opt.value)}
              style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: myTickets === opt.value ? 600 : 400,
                background: myTickets === opt.value ? '#2563EB' : '#fff',
                color: myTickets === opt.value ? '#fff' : '#6B7280',
                transition: 'background .15s, color .15s',
                whiteSpace: 'nowrap',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          className="t360-search"
          placeholder="Search by Ticket ID, VIN, Mobile, Customer..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {/* Show / Hide Filters toggle */}
        {(() => {
          const activeCount = [
            priorityFilters.length > 0,
            statusFilters.length > 0,
            channelFilters.length > 0,
            escalationFilters.length > 0,
            isErtUser && technicianFilters.length > 0,
            !!dateFilter.period,
            catFilters.length > 0,
            subCatFilters.length > 0,
            cityFilters.length > 0,
            stationFilters.length > 0,
            subTicketTypeFilters.length > 0,
          ].filter(Boolean).length
          return (
            <button
              onClick={() => setShowFilters(f => !f)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 6, cursor: 'pointer',
                fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0,
                border: showFilters ? '1px solid #2563EB' : '1px solid #D1D5DB',
                background: showFilters ? '#EFF6FF' : '#fff',
                color: showFilters ? '#2563EB' : '#374151',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {activeCount > 0 && (
                <span style={{
                  background: '#2563EB', color: '#fff', borderRadius: '50%',
                  width: 16, height: 16, fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {activeCount}
                </span>
              )}
            </button>
          )
        })()}

        {currentUser?.feature_access?.includes('admin_page') && (
          <button
            onClick={() => setBulkCloseOpen(true)}
            style={{
              padding: '7px 14px', border: '1px solid #DC2626', borderRadius: 6,
              background: '#FEF2F2', color: '#DC2626', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#DC2626'; e.currentTarget.style.color = '#fff' }}
            onMouseLeave={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626' }}
          >
            Bulk Close
          </button>
        )}
        <div ref={downloadDropRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setDownloadDropOpen(o => !o)}
            disabled={downloadLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', border: '1px solid #16A34A', borderRadius: 6,
              background: downloadDropOpen ? '#16A34A' : '#F0FDF4',
              color: downloadDropOpen ? '#fff' : '#16A34A',
              cursor: downloadLoading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              opacity: downloadLoading ? 0.7 : 1,
            }}
            onMouseEnter={e => { if (!downloadLoading && !downloadDropOpen) { e.currentTarget.style.background = '#16A34A'; e.currentTarget.style.color = '#fff' } }}
            onMouseLeave={e => { if (!downloadDropOpen) { e.currentTarget.style.background = '#F0FDF4'; e.currentTarget.style.color = '#16A34A' } }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download
            <span style={{ fontSize: 9 }}>▼</span>
          </button>
          {downloadDropOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
              background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,.1)', minWidth: 140, overflow: 'hidden',
            }}>
              {[
                { label: '📊 Excel (.xlsx)', value: 'excel' },
                { label: '📄 CSV (.csv)',    value: 'csv'   },
              ].map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleDownload(opt.value)}
                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: '#111827' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <span className="t360-ticket-count">
          {loading ? 'Loading…' : `${totalCount} ticket${totalCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* ── Row 2: collapsible filters ── */}
      {showFilters && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 0 4px' }}>
          <MultiSelectDropdown values={priorityFilters}   onChange={setPriorityFilters}   options={PRIORITY_MULTI_OPTIONS} placeholder="All Priority" />
          <MultiSelectDropdown values={statusFilters}     onChange={setStatusFilters}     options={STATUS_MULTI_OPTIONS} placeholder="All Status" />
          <MultiSelectDropdown values={channelFilters}    onChange={setChannelFilters}    options={CHANNEL_MULTI_OPTIONS} placeholder="All Channels" />
          <MultiSelectDropdown values={escalationFilters} onChange={setEscalationFilters} options={ESCALATION_MULTI_OPTIONS} placeholder="All Escalation" />
          <MultiSelectDropdown values={subTicketTypeFilters} onChange={setSubTicketTypeFilters} options={SUB_TICKET_TYPE_MULTI_OPTIONS} placeholder="All Sub Ticket Type" />
          {isErtUser && (
            <MultiSelectDropdown values={technicianFilters} onChange={setTechnicianFilters} options={technicianOptions} placeholder="All Technicians" />
          )}
          <MultiSelectSearchDropdown
            fetchJson={fetchJson}
            values={catFilters}
            placeholder="All Categories"
            apiUrl="/api/categories/category"
            onChange={(vals) => { setCatFilters(vals); setSubCatFilters([]) }}
          />
          <MultiSelectSearchDropdown
            fetchJson={fetchJson}
            values={subCatFilters}
            placeholder="All Sub-Categories"
            apiUrl={catFilters.length === 1 ? `/api/categories/category?category=${catFilters[0].slug}` : '/api/categories/category'}
            disabled={catFilters.length === 0}
            onChange={setSubCatFilters}
          />
          <MultiSelectSearchDropdown
            fetchJson={fetchJson}
            values={cityFilters}
            placeholder="All Cities"
            apiUrl="/api/geographies/geography/cities"
            onChange={(vals) => { setCityFilters(vals); setStationFilters([]) }}
          />
          <MultiSelectSearchDropdown
            fetchJson={fetchJson}
            values={stationFilters}
            placeholder="All Stations"
            apiUrl={cityFilters.length === 1 ? `/api/geographies/station/?city=${cityFilters[0].slug}` : '/api/geographies/station/'}
            disabled={cityFilters.length === 0}
            onChange={setStationFilters}
          />

          {/* Date filter */}
          <div ref={dateRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setDateOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 10px', background: dateFilter.period ? '#E5F4FB' : '#fff',
                border: `1px solid ${dateFilter.period ? '#A8D4EE' : '#E0E7EF'}`,
                borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: dateFilter.period ? '#1A6FA8' : '#5A6672', whiteSpace: 'nowrap',
              }}
            >
              📅 {dateFilter.label}
              <span style={{ fontSize: 9 }}>▼</span>
            </button>
            {dateOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100,
                background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,.1)', minWidth: 220,
              }}>
                <div style={{ padding: '6px 0', borderBottom: '1px solid #E0E7EF' }}>
                  <div
                    onClick={() => { setDateFilter({ period: '', label: 'All Dates', fromDate: '', toDate: '' }); setDateOpen(false) }}
                    style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: !dateFilter.period ? '#0090D9' : '#2B2B2B', background: !dateFilter.period ? '#E5F4FB' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = !dateFilter.period ? '#E5F4FB' : 'transparent'}
                  >All Dates</div>
                  {DATE_OPTIONS.map(opt => (
                    <div key={opt.period} onClick={() => selectPeriod(opt)}
                      style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: dateFilter.period === opt.period ? '#0090D9' : '#2B2B2B', background: dateFilter.period === opt.period ? '#E5F4FB' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = dateFilter.period === opt.period ? '#E5F4FB' : 'transparent'}
                    >{opt.label}</div>
                  ))}
                </div>
                <div style={{ padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>Custom Range</div>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 11, color: '#5A6672', display: 'block', marginBottom: 3 }}>From</label>
                    <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                      style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: 4, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 11, color: '#5A6672', display: 'block', marginBottom: 3 }}>To</label>
                    <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                      style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: 4, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <button onClick={applyCustom} style={{ width: '100%', padding: '6px', background: '#0090D9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
                </div>
              </div>
            )}
          </div>

          <button className="t360-clear-btn" onClick={handleClear}>Clear</button>
        </div>
      )}

      <div className="t360-table-wrap" style={{ position: 'relative' }}>
        {loading && tickets.length > 0 && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(248,250,252,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#fff', border: '1px solid #E2E8F0',
              borderRadius: 10, padding: '10px 20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              fontSize: 13, fontWeight: 500, color: '#374151',
            }}>
              <div style={{
                width: 16, height: 16, border: '2px solid #E2E8F0',
                borderTop: '2px solid #0090D9', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite', flexShrink: 0,
              }} />
              Loading tickets…
            </div>
          </div>
        )}
        <table className="t360-table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Type</th>
              <th>Customer</th>
              <th>Vehicle VIN</th>
              <th>Category / Sub</th>
              <th>Pri</th>
              <th>Status</th>
              <th>Follow-ups</th>
              {isErtUser && <th>Supervisors</th>}
              <th>Assigned</th>
              <th>Created</th>
              <th>Channel</th>
              <th>Stage</th>
              <th>City</th>
              <th>Station</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && tickets.length === 0 ? (
              <tr>
                <td colSpan={isErtUser ? 16 : 15} className="t360-empty">Loading tickets…</td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td colSpan={isErtUser ? 16 : 15} className="t360-empty">No tickets match your filters.</td>
              </tr>
            ) : (
              tickets.map(t => (
                <tr key={t.ticket_id || t.ticket_number} onClick={() => handleViewTicket(t)} style={{ cursor: 'pointer' }}>
                  <td>
                    <span className="t360-ticket-id">{t.ticket_number}</span>
                  </td>

                  <td>
                    <span style={{ fontSize: 12, color: '#374151' }}>
                      {t.ticket_type ? t.ticket_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                    </span>
                    <span className="t360-customer-id">{t.sub_ticket_type ||''}</span>
                  </td>

                  <td>
                    <span className="t360-customer-name">{t.customer_name || '—'}</span>
                    <span className="t360-customer-id">{t.customer_code || t.customer_phone || ''}</span>
                  </td>

                  <td>
                    <span className="t360-vin-main">{t.vin_number || '—'}</span>
                    <span className="t360-vin-plate">{t.vehicle_registration_number || ''}</span>
                  </td>

                  <td>
                    <span className="t360-cat-main">{t.issue_category || '—'}</span>
                    <span className="t360-cat-sub">{t.issue_sub_category || ''}</span>
                  </td>

                  <td>
                    <span className={`t360-priority ${t.priority || ''}`}>
                      {displayPriority(t.priority)}
                    </span>
                  </td>

                  <td>
                    <span className={`t360-status ${statusClass(t.status)}`}>
                      <span className="t360-status-dot" />
                      {displayStatus(t.status)}
                    </span>
                  </td>

                  <td>
                    {(() => {
                      const count = Array.isArray(t.followups) ? t.followups.length : 0
                      return count > 0 ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 22, height: 20, padding: '0 6px', borderRadius: 10,
                          background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#0090D9',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          {count}
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                      )
                    })()}
                  </td>

                  {isErtUser && (
                    <td>
                      {Array.isArray(t.supervisors) && t.supervisors.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {t.supervisors.map((s, i) => (
                            <span key={i} style={{ fontSize: 12, color: '#374151' }}>{s}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                      )}
                    </td>
                  )}

                  <td>
                    <span className="t360-assigned">{t.assigned_to || '—'}</span>
                  </td>

                  <td>
                    <span className="t360-created-time">{formatCreatedParts(t).time}</span>
                    {formatCreatedParts(t).date && <span className="t360-created-date">{formatCreatedParts(t).date}</span>}
                  </td>

                  <td>
                    <span className="t360-channel">
                      <span className="t360-channel-icon">{channelIcon(t.channel)}</span>
                      <span>{displayChannel(t.channel)}</span>
                    </span>
                  </td>

                  <td>
                    <span className={`t360-esc ${(t.ticket_stage || '').toLowerCase()}`}>
                      {displayStage(t.ticket_stage)}
                    </span>
                  </td>

                  <td>
                    <span className="t360-customer-name">{t.city_name || '—'}</span>
                  </td>

                  <td>
                    <span className="t360-customer-name">{t.station_code || '—'}</span>
                  </td>

                  <td>
                    <button className="t360-view-btn" onClick={e => { e.stopPropagation(); handleViewTicket(t) }}>View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="t360-pagination">
        {totalPages > 1 && (
          <>
            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1 || loading}
            >
              ← Prev
            </button>

            {buildPageNumbers(page, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="t360-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`t360-page-btn${p === page ? ' active' : ''}`}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages || loading}
            >
              Next →
            </button>

            <span className="t360-page-info">
              Page {page} of {totalPages}
            </span>
          </>
        )}

        <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
          Total: <strong style={{ color: '#111827' }}>{totalCount}</strong> ticket{totalCount !== 1 ? 's' : ''}
        </span>

        {/* Page size selector — always visible */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Rows per page:</span>
          <div className="t360-select-wrap" style={{ minWidth: 70 }}>
            <select
              className="t360-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              disabled={loading}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="t360-select-arrow">▼</span>
          </div>
        </div>
      </div>

      {/* Download processing popup */}
      {downloadLoading && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '32px 36px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 280,
          }}>
            <div style={{
              width: 40, height: 40, border: '3px solid #E2E8F0',
              borderTop: '3px solid #16A34A', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Processing download request</div>
            <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'center' }}>
              Preparing your file. This may take a few seconds…
            </div>
          </div>
        </div>
      )}

      {/* Bulk Close confirmation modal */}
      {bulkCloseOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 28px 24px',
            width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>Bulk Close Tickets</h3>
            </div>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              All of these tickets will close. This action cannot be reversed.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setBulkCloseOpen(false)}
                disabled={bulkClosing}
                style={{
                  padding: '8px 18px', border: '1px solid #D1D5DB', borderRadius: 6,
                  background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkClose}
                disabled={bulkClosing}
                style={{
                  padding: '8px 18px', border: 'none', borderRadius: 6,
                  background: bulkClosing ? '#FCA5A5' : '#DC2626', color: '#fff',
                  cursor: bulkClosing ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                {bulkClosing ? 'Closing…' : 'Yes, Close All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
